import React, { useCallback, useEffect, useRef, useState } from 'react'
import { createRoot } from 'react-dom/client'
import { PiPSessionWindow } from '../components/PictureInPicture'

export interface PiPMessage {
  type: string
  data: {
    isAvatarTalking?: boolean
    isFileUploading?: boolean
    shareScreenState?: boolean
  }
}

interface PiPSyncState {
  // Audio state
  volume: number
  isMuted: boolean
  // shareScreenState
  isScreenSharing: boolean

  // Microphone state
  isMicMuted: boolean

  // Avatar state
  avatarReady: boolean

  // Session state
  companionName: string
  avatarUrl?: string

  // Upload state
  isFileUploading: boolean

  // Reminder state (for future use)
  reminderVisible?: boolean
  reminderMessage?: string
}

interface UseSessionPiPProps {
  videoElement: HTMLVideoElement | null
  isSessionActive: boolean
  hasMediaStream?: boolean
  audioRef?: React.RefObject<HTMLAudioElement | null>
  initialVolume?: number
  initialMuted?: boolean
  personaData?: {
    firstName: string
    lastName: string
    avatar?: {
      videos: Array<{ preview_url?: string }>
    }
  } | null
  // Current state for sync
  isAvatarTalking?: boolean
  avatarReady?: boolean
  // Callbacks
  onVolumeChange?: (volume: number) => void
  onMuteChange?: (muted: boolean) => void
  onMicMuteChange?: (micMuted: boolean) => void
  onPiPEnter?: () => void
  onPiPExit?: (finalState: Partial<PiPSyncState>) => void
  onEndSessionComplete?: () => void
  isMuted?: boolean
  muteMicrophone?: (muted: boolean) => void
  userMicrophoneStream?: MediaStream | null
  // Screen sharing
  startScreenShare?: () => Promise<void>
  stopScreenShare?: () => void
  isScreenSharing: boolean

  // Upload controls
  isAuthenticated?: boolean
  avatarId?: string
  organizationId?: string
  // Upload state
  isFileUploading?: boolean

  // Stop talking controls
  onStopTalking?: () => void
  dataChannel?: RTCDataChannel | null
  sessionId?: string
  onTrackUploadEvent?: (event: string) => void
  hideScreenShareButton: boolean
  isWatchSession: boolean
  socketId: string
}

interface UseSessionPiPReturn {
  isPiPActive: boolean
  isPiPSupported: boolean
  enterPiP: () => Promise<void>
  exitPiP: () => void
  togglePiP: () => Promise<void>
  sendMessageToPiP: (message: PiPMessage) => void
}

export const useSessionPiP = ({
  videoElement,
  isSessionActive,
  hasMediaStream = false,
  audioRef,
  initialVolume = 1,
  initialMuted = false,
  personaData,
  isAvatarTalking = false,
  avatarReady = false,
  onVolumeChange,
  onMuteChange,
  onMicMuteChange,
  onPiPEnter,
  onPiPExit,
  onEndSessionComplete,
  isMuted,
  muteMicrophone,
  userMicrophoneStream,
  isScreenSharing = false,
  startScreenShare,
  stopScreenShare,
  isAuthenticated = false,
  avatarId,
  organizationId,
  isFileUploading = false,
  onStopTalking,
  dataChannel,
  sessionId,
  onTrackUploadEvent,
  hideScreenShareButton,
  isWatchSession,
  socketId,
}: UseSessionPiPProps): UseSessionPiPReturn => {
  const [isPiPActive, setIsPiPActive] = useState(false)
  const [isPiPSupported, setIsPiPSupported] = useState(false)
  const isAutoEnteringPiP = useRef(false)
  const pipWindowRef = useRef<Window | null>(null)
  const reactRootRef = useRef<ReturnType<typeof createRoot> | null>(null)

  // Send real-time updates to PiP window
  const sendMessageToPiP = useCallback((message: PiPMessage) => {
    if (pipWindowRef.current && !pipWindowRef.current.closed) {
      pipWindowRef.current.postMessage(message, '*')
    }
  }, [])

  // Collect current state for PiP
  const getCurrentState = useCallback((): PiPSyncState => {
    const currentVolume = audioRef?.current?.volume ?? initialVolume
    const currentMuted = audioRef?.current?.muted ?? initialMuted
    const isMicMuted = userMicrophoneStream
      ? userMicrophoneStream.getAudioTracks().some(track => !track.enabled)
      : false

    return {
      volume: currentVolume,
      isMuted: currentMuted,
      isMicMuted,
      avatarReady,
      companionName: personaData
        ? `${personaData.firstName} ${personaData.lastName}`
        : '',
      avatarUrl: personaData?.avatar?.videos?.[0]?.preview_url,
      isFileUploading,
      isScreenSharing,
    }
  }, [
    audioRef,
    initialVolume,
    initialMuted,
    avatarReady,
    personaData,
    userMicrophoneStream,
    isFileUploading,
    isScreenSharing,
  ])

  // Apply state changes from PiP
  const applyStateChanges = useCallback(
    (finalState: Partial<PiPSyncState>) => {
      if (audioRef?.current && finalState.volume !== undefined) {
        audioRef.current.volume = finalState.volume
        onVolumeChange?.(finalState.volume)
      }

      if (audioRef?.current && finalState.isMuted !== undefined) {
        audioRef.current.muted = finalState.isMuted
        onMuteChange?.(finalState.isMuted)
      }

      if (finalState.isMicMuted !== undefined) {
        if (muteMicrophone) {
          muteMicrophone(finalState.isMicMuted)
        }
        onMicMuteChange?.(finalState.isMicMuted)
      }

      // Sync upload states back to main page
    },
    [
      audioRef,
      onVolumeChange,
      onMuteChange,
      onMicMuteChange,
      muteMicrophone,
      isFileUploading,
    ],
  )

  // Check if Document PiP is supported
  useEffect(() => {
    const checkPiPSupport = () => {
      const supported = Boolean(
        'documentPictureInPicture' in window &&
          window.documentPictureInPicture &&
          'requestWindow' in window.documentPictureInPicture,
      )

      setIsPiPSupported(supported)
    }

    checkPiPSupport()
  }, [])

  // Handle PiP events
  useEffect(() => {
    if (!videoElement || !isPiPSupported) return

    const handleEnterPiP = () => {
      setIsPiPActive(true)
      onPiPEnter?.()
    }

    const handleExitPiP = () => {
      setIsPiPActive(false)
      isAutoEnteringPiP.current = false
      // Pass empty state since this is just video PiP exit, not our custom PiP
      onPiPExit?.({})
    }

    videoElement.addEventListener('enterpictureinpicture', handleEnterPiP)
    videoElement.addEventListener('leavepictureinpicture', handleExitPiP)

    return () => {
      videoElement.removeEventListener('enterpictureinpicture', handleEnterPiP)
      videoElement.removeEventListener('leavepictureinpicture', handleExitPiP)
    }
  }, [videoElement, isPiPSupported, onPiPEnter, onPiPExit])

  const createPiPContent = useCallback(
    (pipWindow: Window, currentVideoElement: HTMLVideoElement | null) => {
      const companionName = personaData
        ? `${personaData.firstName} ${personaData.lastName}`
        : 'Session'

      // Set document title
      pipWindow.document.title = `${companionName} - Session`

      // Copy stylesheets from main window
      const copyStyleSheets = () => {
        ;[...document.styleSheets].forEach(styleSheet => {
          try {
            const cssRules = [...styleSheet.cssRules]
              .map(rule => rule.cssText)
              .join('')
            const style = pipWindow.document.createElement('style')
            style.textContent = cssRules
            pipWindow.document.head.appendChild(style)
          } catch {
            const link = pipWindow.document.createElement('link')
            link.rel = 'stylesheet'
            link.type = styleSheet.type || 'text/css'
            link.media = styleSheet.media.mediaText
            link.href = styleSheet.href || ''
            pipWindow.document.head.appendChild(link)
          }
        })
      }

      // Create React root and render component
      const renderReactComponent = () => {
        // Create a container div for React
        const container = pipWindow.document.createElement('div')
        container.id = 'pip-react-root'
        pipWindow.document.body.appendChild(container)

        // Create React root
        const root = createRoot(container)
        reactRootRef.current = root

        // Get current state for sync
        const currentState = getCurrentState()

        // Render the PiP component
        root.render(
          React.createElement(PiPSessionWindow, {
            isWatchSession,
            isMuted,
            pipWindow,
            videoElement: currentVideoElement,
            audioRef,
            initialVolume: currentState.volume,
            initialMuted: currentState.isMuted,
            initialIsScreenSharing: currentState.isScreenSharing,
            onVolumeChange,
            onMuteChange,
            onEndSessionComplete,
            muteMicrophone,
            startScreenShare,
            stopScreenShare,
            // Upload props
            isAuthenticated,
            avatarId,
            organizationId,
            // Upload state sync
            initialIsFileUploading: currentState.isFileUploading,
            // Stop talking props
            onStopTalking,
            dataChannel,
            sessionId,
            onTrackUploadEvent,
            hideScreenShareButton,
            socketId,
          }),
        )
      }

      // Build the PiP window content
      copyStyleSheets()
      renderReactComponent()
    },
    [
      personaData,
      isMuted,
      muteMicrophone,
      userMicrophoneStream,
      startScreenShare,
      stopScreenShare,
      onStopTalking,
      dataChannel,
      sessionId,
      hideScreenShareButton,
    ],
  )

  const enterPiP = useCallback(async (): Promise<void> => {
    // Check if window is actually closed even if state says it's active
    if (pipWindowRef.current && pipWindowRef.current.closed) {
      pipWindowRef.current = null
      setIsPiPActive(false)
    }

    if (!isPiPSupported) {
      throw new Error('Cannot enter PiP: not supported')
    }

    if (isPiPActive && pipWindowRef.current && !pipWindowRef.current.closed) {
      throw new Error('Cannot enter PiP: already active')
    }

    const pipWindow = await window.documentPictureInPicture!.requestWindow({
      width: 400,
      height: 300,
      disallowReturnToOpener: false,
    })

    pipWindowRef.current = pipWindow

    // Create PiP window content using DOM API
    createPiPContent(pipWindow, videoElement)

    // Clone video stream to PiP window
    if (videoElement && videoElement.srcObject) {
      const pipVideo = pipWindow.document.getElementById(
        'pipVideo',
      ) as HTMLVideoElement
      if (pipVideo) {
        pipVideo.srcObject = videoElement.srcObject
      }
    }

    // Handle PiP window close
    pipWindow.addEventListener('beforeunload', () => {
      // Collect final state before closing
      const finalState = getCurrentState()
      pipWindowRef.current = null
      setIsPiPActive(false)
      applyStateChanges(finalState)
      onPiPExit?.(finalState)
    })

    setIsPiPActive(true)
    onPiPEnter?.()

    // Send initial avatar talking state to PiP window
    // Use setTimeout to ensure PiP window is ready to receive messages
    setTimeout(() => {
      sendMessageToPiP({
        type: 'avatarTalkingStateChanged',
        data: { isAvatarTalking },
      })
    }, 100)
  }, [
    isPiPSupported,
    isPiPActive,
    videoElement,
    createPiPContent,
    getCurrentState,
    applyStateChanges,
    onPiPEnter,
    onPiPExit,
    onEndSessionComplete,
    sendMessageToPiP,
    isAvatarTalking,
  ])

  const exitPiP = useCallback((): void => {
    if (!isPiPActive || !pipWindowRef.current) {
      throw new Error('Cannot exit PiP: not currently active')
    }

    try {
      // Collect final state before closing
      const finalState = getCurrentState()

      // Unmount React component asynchronously
      if (reactRootRef.current) {
        const root = reactRootRef.current
        reactRootRef.current = null

        // Defer unmounting to avoid race condition
        setTimeout(() => {
          root.unmount()
        }, 0)
      }

      pipWindowRef.current.close()
      pipWindowRef.current = null
      setIsPiPActive(false)
      applyStateChanges(finalState)
      onPiPExit?.(finalState)
    } catch (error) {
      console.log('error', error)
      throw error
    }
  }, [isPiPActive, getCurrentState, applyStateChanges, onPiPExit])

  const togglePiP = useCallback(async (): Promise<void> => {
    if (isPiPActive) {
      exitPiP()
    } else {
      await enterPiP()
    }
  }, [isPiPActive, enterPiP, exitPiP])

  // Register Media Session API handler for automatic PiP
  useEffect(() => {
    if (!isPiPSupported || !isSessionActive || !hasMediaStream) return

    const handleEnterPiP = async () => {
      // Double-check state before attempting PiP
      if (pipWindowRef.current && pipWindowRef.current.closed) {
        pipWindowRef.current = null
        setIsPiPActive(false)
      }

      try {
        await enterPiP()
      } catch (error) {
        console.log('error', error)
      }
    }

    try {
      if (
        'mediaSession' in navigator &&
        'setActionHandler' in navigator.mediaSession
      ) {
        navigator.mediaSession.setActionHandler('enterpictureinpicture', () => {
          handleEnterPiP().catch(console.error)
        })
      }
    } catch (error) {
      console.log('Media Session API not supported:', error)
    }

    return () => {
      if (
        'mediaSession' in navigator &&
        'setActionHandler' in navigator.mediaSession
      ) {
        navigator.mediaSession.setActionHandler('enterpictureinpicture', null)
      }
    }
  }, [isPiPSupported, isSessionActive, hasMediaStream, enterPiP])

  // Additional visibility change handler to clean up state
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (
        !document.hidden &&
        pipWindowRef.current &&
        pipWindowRef.current.closed
      ) {
        pipWindowRef.current = null
        setIsPiPActive(false)
      }
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [])

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      // Unmount React component asynchronously to avoid race condition
      if (reactRootRef.current) {
        const root = reactRootRef.current
        reactRootRef.current = null

        // Use setTimeout to defer unmounting until after current render
        setTimeout(() => {
          root.unmount()
        }, 0)
      }

      if (pipWindowRef.current && !pipWindowRef.current.closed) {
        pipWindowRef.current.close()
        pipWindowRef.current = null
      }
    }
  }, [])

  // Send real-time avatar talking state updates to PiP window
  useEffect(() => {
    if (isPiPActive) {
      sendMessageToPiP({
        type: 'avatarTalkingStateChanged',
        data: { isAvatarTalking },
      })
    }
  }, [isAvatarTalking, isPiPActive, sendMessageToPiP])

  return {
    isPiPActive,
    isPiPSupported,
    enterPiP,
    exitPiP,
    togglePiP,
    sendMessageToPiP,
  }
}
